<template>
  <v-dialog
    :value="show"
    @input="close()"
    max-width="800"
    persistent
  >
    <v-card>
      <v-container fluid>
        <v-row class="flex-column" style="position: relative;">
          <v-btn 
            x-small 
            text 
            @click="close"
            style="position: absolute; bottom: 5px; right: 5px; z-index: 3;"
            :disabled="saving"
          >
            Cancel
          </v-btn>

          <v-col class="py-0">
            <v-stepper
              v-model="step"
              vertical
              class="elevation-0"
            >
              <v-stepper-step
                :complete="step > 1"
                step="1"
              >
                <span>
                  <span :class="{'caption grey--text':step > 1}">Choose A Type</span>
                  <div v-if="step > 1"><b>{{ type.charAt(0).toUpperCase() + type.substring(1) }}</b></div>
                </span>
              </v-stepper-step>
              <v-stepper-content step="1">
                <div class="title text-center">What type of debt is this?</div>
                <v-container fluid>
                  <v-row class="justify-center align-center">
                    <v-col
                      cols="3"
                    >
                      <v-btn class="debtButton" @click="type = 'mortgage'">
                        <v-icon>mdi-home</v-icon>
                        <div>Mortgage</div>
                      </v-btn>
                    </v-col>
                    <v-col
                      cols="3"
                    >
                      <v-btn class="debtButton" @click="type = 'auto'">
                        <v-icon>mdi-car</v-icon>
                        <div>Auto</div>
                      </v-btn>
                    </v-col>
                    <v-col
                      cols="3"
                    >
                      <v-btn class="debtButton" @click="type = 'credit'">
                        <v-icon>mdi-credit-card</v-icon>
                        <div>Card</div>
                      </v-btn>
                    </v-col>
                    <v-col
                      cols="3"
                    >
                      <v-btn class="debtButton" @click="type = 'other'">
                        <v-icon>mdi-bank</v-icon>
                        <div>Other</div>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-container>
              </v-stepper-content>

              <v-stepper-step
                :complete="step > 1.1"
                step="1.1"
                v-if="(type == 'mortgage' || type == 'other')"
              >
                <span>
                  <span :class="{'caption grey--text':step > 1.1}">Interest Only</span>
                  <div v-if="step > 1.1"><b>{{ interest_only ? 'Yes' : 'No' }}</b></div>
                </span>
              </v-stepper-step>
              <v-stepper-content step="1.1">
                <div class="title text-center">Is this an interest only loan?</div>
                <v-container fluid>
                  <v-row class="justify-center align-center">
                    <v-col
                      cols="3"
                    >
                      <v-btn class="debtButton" @click="interest_only = 1">
                        <div>Yes</div>
                      </v-btn>
                    </v-col>
                    <v-col
                      cols="3"
                    >
                      <v-btn class="debtButton" @click="interest_only = 0">
                        <div>No</div>
                      </v-btn>
                    </v-col>
                  </v-row>
                  <v-row class="mt-2 justify-center">
                    <v-btn small text color="primary" @click="goBack">Back</v-btn>
                  </v-row>
                </v-container>
              </v-stepper-content>

              <v-stepper-step
                :complete="step > 1.2"
                step="1.2"
                v-if="interest_only"
              >
                <span>
                  <span :class="{'caption grey--text':step > 1.2}">Interest Only Details</span>
                  <div v-if="step > 1.2"><b>{{ ( Math.round(loan_months / 12 * 100) / 100 ) + ' Year Loan - ' + ( Math.round(interest_only_months / 12 * 100) / 100 ) + ' Years Interest Free' }}</b></div>
                </span>
              </v-stepper-step>
              <v-stepper-content step="1.2">
                <div class="title text-center">Please provide the following details</div>
                <v-container fluid>
                  <v-row class="justify-center align-center">
                    <v-col
                      cols="3"
                    >
                      <v-menu
                        ref="debtStartMenu"
                        v-model="debtStartMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="loan_start_date_formatted"
                            dense
                            outlined
                            label="Loan Start Month"
                            placeholder="Choose..."
                            v-bind="attrs"
                            v-on="on"
                            @blur="loan_start_date = parseDate(loan_start_date_formatted)"
                          />
                        </template>
                        <v-date-picker v-model="loan_start_date" no-title scrollable @input="debtStartMenu = false" type="month" />
                      </v-menu>
                    </v-col>
                    <v-col
                      cols="3"
                    >
                      <v-text-field
                        v-model="loan_start_balance"
                        dense
                        outlined
                        placeholder="$0"
                        label="Original Loan Balance"
                      />
                    </v-col>
                    <v-col
                      cols="3"
                    >
                      <v-text-field
                        v-model="loan_months"
                        dense
                        outlined
                        :hint="(loan_months ? ( Math.round(loan_months / 12 * 100) / 100 ) + ' Years' : null)"
                        persistent-hint
                        placeholder="In Months"
                        label="Loan Length"
                      />
                    </v-col>

                    <v-col
                      cols="3"
                    >
                      <v-text-field
                        v-model="interest_only_months"
                        dense
                        outlined
                        :hint="(interest_only_months ? ( Math.round(interest_only_months / 12 * 100) / 100 ) + ' Years' : null)"
                        persistent-hint
                        placeholder="In Months"
                        label="Interest Only Length"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="mt-2 justify-center">
                    <v-btn small text color="primary" @click="goBack">Back</v-btn>
                    <v-btn small color="primary" class="ml-2" :disabled="!canContinue" @click="nextStep">Continue</v-btn>
                  </v-row>
                </v-container>
              </v-stepper-content>

              <v-stepper-step
                :complete="step > 2"
                step="2"
              >
                <span>
                  <span :class="{'caption grey--text':step > 2}">Enter Balance</span>
                  <div v-if="step > 2"><b>{{ balance | currencyRounded }}</b></div>
                </span>
              </v-stepper-step>
              <v-stepper-content step="2">
                <div class="title text-center">What is the current balance?</div>
                <v-container fluid>
                  <v-row class="justify-center align-center">
                    <v-col
                      cols="12"
                    >
                      <v-text-field
                        v-model="balance"
                        dense
                        outlined
                        hide-details
                        placeholder="0"
                        clearable
                      />
                    </v-col>
                  </v-row>
                  <v-row class="mt-2 justify-center">
                    <v-btn small text color="primary" @click="goBack">Back</v-btn>
                    <v-btn small color="primary" class="ml-2" :disabled="!canContinue" @click="nextStep">Continue</v-btn>
                  </v-row>
                </v-container>
              </v-stepper-content>

              <v-stepper-step
                :complete="step > 3"
                step="3"
              >
                <span>
                  <span :class="{'caption grey--text':step > 3}">Enter Loan Details</span>
                  <div v-if="step > 3"><b>{{ interest_rate }}% - {{ minimum_payment | currencyRounded }} - {{ (actual_payment || minimum_payment) | currencyRounded}}</b></div>
                </span>
              </v-stepper-step>
              <v-stepper-content step="3">
                <div class="title text-center">
                  <span v-if="interest_only">What is the interest rate?</span>
                  <span v-else>What are the interest and payment details?</span>
                </div>
                <v-container fluid>
                  <v-row class="justify-center align-center">
                    <v-col
                      cols="4"
                    >
                      <v-text-field
                        v-model="interest_rate"
                        dense
                        outlined
                        hide-details
                        placeholder="0"
                        label="Interest Rate"
                      />
                    </v-col>
                    <v-col
                      cols="4"
                      v-if="!interest_only"
                    >
                      <v-text-field
                        v-model="minimum_payment"
                        dense
                        outlined
                        hide-details
                        placeholder="0"
                        label="Minimum Payment"
                      />
                    </v-col>

                    <v-col
                      cols="4"
                      v-if="!interest_only"
                    >
                      <v-text-field
                        v-model="actual_payment"
                        dense
                        outlined
                        hide-details
                        :placeholder="minimum_payment || '0'"
                        label="Actual Payment"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="mt-2 justify-center">
                    <v-btn small text color="primary" @click="goBack">Back</v-btn>
                    <v-btn small color="primary" class="ml-2" :disabled="!canContinue" @click="nextStep">Continue</v-btn>
                  </v-row>
                </v-container>
              </v-stepper-content>

              <v-stepper-step
                :complete="step > 4"
                step="4"
              >
                <span>
                  <span :class="{'caption grey--text':step > 4}">Add A Name</span>
                  <div v-if="step > 4"><b>{{ name }}</b></div>
                </span>
              </v-stepper-step>
              <v-stepper-content step="4">
                <div class="title text-center">What would you like to name this debt?</div>
                <v-container fluid>
                  <v-row class="justify-center align-center">
                    <v-col
                      cols="12"
                    >
                      <v-text-field
                        v-model="name"
                        dense
                        outlined
                        hide-details
                        clearable
                      />
                    </v-col>
                  </v-row>
                  <v-row class="mt-2 justify-center">
                    <v-btn small text color="primary" @click="goBack" :disabled="saving">Back</v-btn>
                    <v-btn small color="primary" class="ml-2" :disabled="!canContinue" :loading="saving" @click="saveDebt">Save</v-btn>
                  </v-row>
                </v-container>
              </v-stepper-content>

            </v-stepper>
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'DebtWizard',
  props: {
    household: {
      type: Object,
      required: true
    },
    useUrl: Boolean
  },
  data () {
    return {
      step: 1,
      forceShow: false,

      name: null,
      type: null,
      cardType: null,
      balance: null,
      balance_as_of: null,
      balance_as_of_formatted: null,
      minimum_payment: null,
      actual_payment: null,
      interest_rate: null,

      interest_only: false,
      loan_months: null,
      interest_only_months: null,
      loan_start_date: null,
      loan_start_date_formatted: null,
      loan_start_balance: null,

      debtStartMenu: false,
      balanceAsOfMenu: false,
      changeClientData: false,
      saving: false
    }
  },
  mounted () {
    this.reset()

    if(this.$route.params.token){
      this.changeClientData = true;
    }
  },
  watch: {
    show(newVal) {
      if(newVal) this.reset()
    },
    loan_start_date ( newVal ) {
      this.loan_start_date_formatted = this.formatDate( newVal )
    },
    balance_as_of ( newVal ) {
      this.balance_as_of_formatted = this.formatDate( newVal )
    },
    
    type(newVal) {
      if(newVal == 'mortgage' || newVal == 'other') {
        this.$nextTick(() => {
          this.step = 1.1
        })
      } else if(newVal) {
        this.step = 2
      }
    },
    interest_only(newVal) {
      if(newVal === 0) this.step = 2
      if(newVal === 1) this.$nextTick(() => {this.step = 1.2})
    }
  },
  computed: {
    show() {
      if(this.forceShow) return true
      if(this.useUrl && (this.$route.name == 'households-debts' && this.$route.params.debt_action == 'add')) return true
      return false
    },
    canContinue() {
      return this.stepComplete(this.step)
    }
  },
  methods: {
    close () {
      if(this.forceShow) {
        this.forceShow = false
      } else {
        this.$router.push({params:{debt_id: null, debt_action: null}})
      }
    },
    open () {
      this.forceShow = true
    },
    reset () {
      this.step                     = 1
      this.name                     = null
      this.type                     = null
      this.cardType                 = null
      this.balance                  = null
      this.balance_as_of            = null
      this.minimum_payment          = null
      this.actual_payment           = null
      this.interest_rate            = null
      this.interest_only            = false
      this.loan_start_date          = null
      this.loan_months              = null
      this.interest_only_months     = null
      this.loan_start_balance       = null

      this.saving                   = false
    },
    saveDebt () {
      this.saving = true;
      //let balAsOf = (this.balance_as_of ? this.balance_as_of + '-05' : this.$moment().format('YYYY-MM') + '-05')
      let balAsOf = this.$moment().format('YYYY-MM') + '-05'
      if(this.interest_only) this.actual_payment = null
      
      let data = {
        name:                 this.name,
        type:                 this.type,
        balance:              this.balance,
        balance_as_of:        balAsOf,
        minimum_payment:      (this.interest_only ? 1 : this.minimum_payment),
        actual_payment:       (this.actual_payment || null),
        interest_rate:        this.interest_rate,
        household_id:         this.household.id,
        interest_only:        (this.interest_only ? 1 : 0),
        start_balance:        (this.interest_only ? this.loan_start_balance : null),
        loan_months:          (this.interest_only ? this.loan_months : null),
        interest_only_months: (this.interest_only ? this.interest_only_months : null),
        start_date:           (this.interest_only ? this.loan_start_date + '-05' : null)
      }
      let url = 'debts?expand=amortizationSchedule';

      if (this.changeClientData){
        url = 'household-customer-data/add-debt'
      }

      this.$http.post(url, data)
        .then(response => {
          this.$emit('on-save', response.data)
          this.close()
        })
        .catch(err => {
          if(err.response.status == 401) return

          let message = ''
          if(err.response.status == 422) {
            message = ':<br><br>'
            for(let i = 0; i < err.response.data.length; i++) {
              message += '<b>' + err.response.data[i].message + '</b><br>'
            }
          }
          console.log('Debt Error: ', err.response);
          this.$swal.fire({
            title: 'Error',
            html: 'There was an issue saving the debt' + message,
            type: 'error'
          });
        })
        .finally(() => {
          this.saving = false;
        })
    },
    formatDate (date) {
      if (!date) return null

      const [year, month] = date.split('-')
      return `${month}/${year}`
    },
    parseDate (date) {
      if (!date) return null

      let [month, year, year2] = date.split('/')
      if(year2) year = year2
      return `${year}-${month.padStart(2, '0')}`
    },
    goBack() {
      if(this.step == 1.1) {
        this.type = null
        this.step = 1
      } else if(this.step == 1.2) {
        this.interest_only = null
        this.step = 1.1
      } else if(this.step == 2) {
        if(this.interest_only) {
          this.step = 1.2
        } else if(this.type == 'mortgage' || this.type == 'other') {
          this.interest_only = null
          this.step = 1.1
        } else {
          this.type = null
          this.step = 1
        }
      } else {
        this.step--;
      }
    },
    nextStep() {
      if(!this.canContinue) return
      if(this.step == 1.2) {
        this.step = 2
      } else {
        this.step++
      }
    },
    stepComplete(step) {
      if(step == 1) {
        if(this.type !== null) return true
      }
      if(step == 1.2) {
        if(this.loan_start_date && this.loan_months && this.interest_only_months && this.loan_start_balance) return true
      }
      if(step == 2) {
        if(this.balance > 0) return true
      }
      if(this.step == 3) {
        if(this.interest_only && this.interest_rate) return true
        if(this.interest_rate !== null && (this.interest_rate + '').trim() != '' && this.minimum_payment !== null && (this.minimum_payment + '').trim() != '') return true
      }
      if(this.step == 4) {
        if((this.name + '').trim() != '' && this.name !== null) return true
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
  .darkButtonPrimary {
    ::v-deep .v-btn__content {
      ::v-deep i {
        color: #FFF !important;
      }
      color: #FFF !important;
    }
  }

  .debtButton {
    padding: unset !important;
    padding-top: 100% !important;
    height: unset !important;
    min-height: unset !important;
    max-height: unset !important;
    position: relative;
    width: 100% !important;
    ::v-deep i {
      font-size: 50px;
      margin-bottom: 5px;
    }
    ::v-deep .v-btn__content {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      flex-direction: column;
    }
  }
</style>